/**
 * DomainRouter — Renders different content based on the current hostname.
 *
 * www.openhubr.com / openhubr.com → Landing page (marketing)
 * app.openhubr.com               → Application (authenticated)
 * localhost                       → Both (development)
 */

import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

const APP_DOMAIN = import.meta.env.VITE_APP_DOMAIN || "app.openhubr.com";

function isAppDomain(): boolean {
  const hostname = window.location.hostname;
  // Development: allow both
  if (hostname === "localhost" || hostname === "127.0.0.1") return true;
  // App subdomain
  return hostname.startsWith("app.") || hostname === APP_DOMAIN;
}

function isMarketingDomain(): boolean {
  const hostname = window.location.hostname;
  // Development: allow both
  if (hostname === "localhost" || hostname === "127.0.0.1") return true;
  // Marketing: www or root domain (not app.)
  return !hostname.startsWith("app.");
}

/**
 * Wraps landing/marketing routes — only renders on www/root domain.
 * On app subdomain, redirects to /dashboard.
 */
export function MarketingRoute({ children }: { children: ReactNode }) {
  if (!isMarketingDomain()) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

/**
 * Wraps app routes — only renders on app subdomain.
 * On marketing domain, redirects to app subdomain.
 */
export function AppRoute({ children }: { children: ReactNode }) {
  const location = useLocation();

  if (!isAppDomain()) {
    // Redirect to app subdomain preserving the path
    const appUrl = `https://${APP_DOMAIN}${location.pathname}${location.search}`;
    window.location.href = appUrl;
    return null;
  }
  return <>{children}</>;
}

/**
 * Returns the correct URL for the app subdomain.
 * Used in LP CTAs (e.g., "Começar Grátis" → app.openhubr.com/auth)
 */
export function getAppUrl(path: string = "/"): string {
  const hostname = window.location.hostname;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return path; // Same origin in dev
  }
  return `https://${APP_DOMAIN}${path}`;
}

/**
 * Returns the correct URL for the marketing site.
 */
export function getMarketingUrl(path: string = "/"): string {
  const hostname = window.location.hostname;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return path;
  }
  return `https://www.openhubr.com${path}`;
}
